import {Injectable} from '@angular/core';
import {Observable} from "rxjs";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {DataGridServiceInterface} from "./data-grid-service.interface";
import {UserCarePlanDgSearchResults} from "../models/datagrid-search-results/user-care-plan.dg-search-results.model";
import {UserCarePlan} from "../models/user-care-plan.model";
import {UserCarePlanItem} from "../models/user-care-plan-item.model";

/**
 * Serwis warstwy danych obsługujący Plany Abonamentowe (Care Plans) przypisane
 * do kont pacjentów (użytkowników). Zarządza cyklem życia abonamentów,
 * w tym aktywacją, śledzeniem zużycia pakietów (usług) oraz anulacjami.
 */
@Injectable({
  providedIn: 'root'
})
export class UserCarePlanService implements DataGridServiceInterface<Observable<UserCarePlanDgSearchResults>> {

  constructor(private apiService: ApiService, public commonUtils: CommonUtils) {
  }

  /**
   * Pobiera paginowaną listę planów opieki przypisanych do pacjentów,
   * wykorzystywaną do zasilenia głównego widoku tabelarycznego (DataGrid).
   *
   * @param limit Rozmiar strony (maksymalna liczba rekordów).
   * @param offset Przesunięcie względem początku zestawu danych.
   * @param sortOrder Kierunek sortowania wyników ('ASC' lub 'DESC').
   * @param orderBy Kolumna, według której następuje sortowanie.
   * @param showDeletedObjects Flaga określająca uwzględnianie planów zakończonych/anulowanych.
   * @returns Observable emitujący ustandaryzowany obiekt wyników dla tabeli.
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<UserCarePlanDgSearchResults> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/user-care-plans/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new UserCarePlanDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new UserCarePlan(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);

      } else {
        return undefined;
      }
    }));
  }

  /**
   * Filtruje przypisane pakiety opieki na podstawie zdefiniowanych kryteriów.
   *
   * @param limit Rozmiar strony.
   * @param offset Przesunięcie.
   * @param searchFieldsData Mapa zawierająca parametry wyszukiwania.
   * @param sortOrder Kierunek sortowania.
   * @param orderBy Kolumna sortująca.
   * @param showDeletedObjects Flaga widoczności usuniętych rekordów.
   * @returns Observable emitujący przefiltrowaną listę dla DataGrid.
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<UserCarePlanDgSearchResults> {
    const data = {
      limit: limit,
      offset: offset,
      searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
      sortOrder: sortOrder,
      orderBy: orderBy,
      showDeletedObjects: showDeletedObjects
    }
    return this.apiService.post<VetineoApiResponse>(`/dashboard/user-care-plans/search`, data).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        console.log(vetineoApiResponse.response.results);
        return new UserCarePlanDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new UserCarePlan(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Usuwa przypisanie planu opieki z profilu pacjenta.
   *
   * @param uuid Unikalny identyfikator konkretnej instancji przypisanego planu.
   * @returns Observable zwracający status operacji (string 'ok').
   */
  delete(uuid: any) {
    return this.apiService.delete<VetineoApiResponse>(`/dashboard/user-care-plans/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return 'ok';
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Tworzy i przypisuje nową instancję Planu Opieki do konkretnego pacjenta.
   * Tworzy instancję o statusie oczekującym na aktywację.
   *
   * @param userCarePlan Obiekt zawierający parametry konfiguracyjne przypisywanego planu.
   * @returns Observable emitujący utworzony i zapisany obiekt instancji planu z nadanym UUID.
   */
  addUserCarePlan(userCarePlan: UserCarePlan) {
    return this.apiService.post<VetineoApiResponse>('/dashboard/user-care-plans', userCarePlan).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<UserCarePlan>(new UserCarePlan(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Zapisuje modyfikacje w strukturze przypisanego planu opieki.
   *
   * @param userCarePlan Zmodyfikowany obiekt instancji planu.
   * @returns Observable emitujący zaktualizowany obiekt instancji planu.
   */
  updateUserCarePlan(userCarePlan: UserCarePlan) {
    return this.apiService.put<VetineoApiResponse>('/dashboard/user-care-plans', userCarePlan).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<UserCarePlan>(new UserCarePlan(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera pełne szczegóły wybranej instancji planu opieki pacjenta
   *
   * @param uuid Unikalny identyfikator instancji planu.
   * @returns Observable emitujący pełny obiekt instancji planu opieki.
   */
  getUserCarePlanByUUID(uuid: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/user-care-plans/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<UserCarePlan>(new UserCarePlan(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Zmienia status planu opieki na 'Aktywny'.
   *
   * @param uuid Identyfikator przypisanego planu.
   * @returns Observable emitujący zaktywowany obiekt instancji planu.
   */
  activateUserCarePlan(uuid: string): Observable<UserCarePlan> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/user-care-plans/activate/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<UserCarePlan>(new UserCarePlan(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Sprawdza, jaki konkretnie Plan Opieki został zastosowany/wykorzystany podczas
   * realizacji wybranej rezerwacji wizyty.
   *
   * @param uuid Identyfikator odbytej rezerwacji (VetReservation).
   * @returns Observable emitujący obiekt instancji planu (jeśli wizyta z niego korzystała).
   */
  getUserCarePlanByVetReservationUUID(uuid: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/user-care-plans/vet-reservation/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<UserCarePlan>(new UserCarePlan(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }


  /**
   * Oznacza pojedynczą usługę abonamentową jako 'Wykorzystaną' (Zrealizowaną) w kontekście konkretnej wizyty.
   *
   * @param carePlanItemUUID Identyfikator usługi wchodzącej w skład abonamentu (np. 'Szczepienie wścieklizna').
   * @param vetReservationUUID Identyfikator rezerwacji wizyty.
   * @returns Observable emitujący zaktualizowany obiekt zrealizowanej usługi.
   */
  executeCarePlanItemForReservation(carePlanItemUUID: string, vetReservationUUID: string): Observable<UserCarePlanItem> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/user-care-plan-reservation-usage/${carePlanItemUUID}/${vetReservationUUID}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<UserCarePlanItem>(new UserCarePlanItem(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Ręcznie oznacza element pakietu abonamentowego jako wykorzystany.
   * Stosowane, gdy usługa odbywa się poza systemem standardowych rezerwacji.
   *
   * @param carePlanUUID Unikalny identyfikator nadrzędnej instancji planu pacjenta.
   * @param carePlanItemUUID Identyfikator elementu pakietowego.
   * @returns Observable emitujący zaktualizowany obiekt usługi z datą realizacji.
   */
  executeCarePlanItem(carePlanUUID: string, carePlanItemUUID: string): Observable<UserCarePlanItem> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/user-care-plan-usage/${carePlanUUID}/${carePlanItemUUID}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<UserCarePlanItem>(new UserCarePlanItem(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Cofa (anuluje) wykorzystanie pojedynczej usługi z planu abonamentowego, "zwracając" ją do puli
   * pacjenta.
   *
   * @param requestObj Obiekt parametrów anulacji (zawiera identyfikatory i obowiązkowy powód).
   * @returns Observable emitujący zaktualizowany (zwrócony do puli) element pakietu.
   */
  cancelCarePlanItem(requestObj: {
    reason: string;
    vClientId: number;
    carePlanUUID: string;
    userCarePlanItemUUID: string;
    vetReservationUUID: string
  }): Observable<UserCarePlanItem> {
    return this.apiService.post<VetineoApiResponse>('/dashboard/user-care-plan-cancel', requestObj).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<UserCarePlanItem>(new UserCarePlanItem(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }
}
